import { CancelButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import { PageHeader } from '@core/components/PageHeader';
import { SearchBar } from '@core/components/SearchBar';
import { SimpleTable } from '@core/components/Table';
import { TableActionButton, TableActionContainer } from '@core/components/TableAction';
import { Grid } from '@material-ui/core';
import { isEqual } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { FaArrowCircleUp, FaEye } from 'react-icons/fa';
import { AcompanharReguaCobrancaContext } from '../../../AcompanharReguaCobrancaContext';
import { AcompanharReguaCobrancaModais } from '../AcompanharReguaCobrancaModais';
import DetalhesCobrancaModal from '../DetalhesCobranca/DetalhesCobrancaModal';

const getCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value).replace('R$', '');

const noAccents = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const VisualizarCobrancaModal = () => {
  const [modal, setModal] = useContext(AcompanharReguaCobrancaContext).modal;
  const [, setModalDetalhesCobranca] = useContext(AcompanharReguaCobrancaContext).modalDetalhesCobranca;
  const [search, setSearch] = useContext(AcompanharReguaCobrancaContext).search;
  const [loading] = useContext(AcompanharReguaCobrancaContext).loading;
  const [, setPage] = useContext(AcompanharReguaCobrancaContext).page;
  const [, setSize] = useContext(AcompanharReguaCobrancaContext).size;
  const [cardAtual, setCardAtual] = useContext(AcompanharReguaCobrancaContext).cardAtual;
  const [, setDetalhesCobranca] = useContext(AcompanharReguaCobrancaContext).detalheCobranca;

  const cobranca = cardAtual?.contasReceber?.map((conta) => {
    return {
      id: conta.id,
      cliente: conta?.cliente?.pessoa?.nome,
      nossoNumero: conta?.nossoNumeroAntigo || conta?.receitaCobranca?.nossoNumero,
      vencimento: conta?.dataVencimento,
      valor: conta?.valorBruto,
      tipoEnvio: cardAtual?.acoes,
    };
  });

  const [filteredCobranca, setFilteredCobranca] = useState(cobranca);

  const handleVisualizar = (_cobranca) => {
    setDetalhesCobranca(cardAtual?.contasReceber?.find((conta) => conta.id === _cobranca.id));
    setModalDetalhesCobranca(AcompanharReguaCobrancaModais.detalhesCobranca);
  };

  const handleCloseModal = () => {
    setCardAtual({});
    setModal('');
  };

  const formatTipoEnvio = (tipos) => {
    let value = '';
    const valueFormatado = tipos.map((tipo) => {
      switch (tipo) {
        case 'EMAIL':
          value = 'E-mail';
          break;
        case 'SMS':
          value = 'SMS';
          break;
        case 'WHATSAPP':
          value = 'WhatsApp';
          break;
        default:
          break;
      }
      return value;
    });
    return valueFormatado.join(', ');
  };

  const columns = [
    {
      name: 'cliente',
      title: 'Cliente',
      align: 'left',
      hideTooltip: true,
      getCellValue: (cobranca) => {
        return cobranca?.cliente;
      },
      sortingEnabled: false,
    },
    {
      name: 'nossoNumero',
      title: 'Nosso número',
      align: 'left',
      hideTooltip: true,
      getCellValue: (cobranca) => {
        return cobranca?.nossoNumero;
      },
      sortingEnabled: false,
    },
    {
      name: 'vencimento',
      title: 'Vencimento',
      align: 'left',
      hideTooltip: true,
      getCellValue: (cobranca) => {
        return cobranca?.vencimento ? moment(cobranca.vencimento).format('DD/MM/YYYY') : '';
      },
      sortingEnabled: false,
    },
    {
      name: 'valor',
      title: 'Valor (R$)',
      align: 'left',
      hideTooltip: true,
      getCellValue: (cobranca) => {
        return getCurrency(cobranca?.valor);
      },
      sortingEnabled: false,
    },
    {
      name: 'tipoEnvio',
      title: 'Tipo de envio',
      align: 'left',
      hideTooltip: true,
      getCellValue: (cobranca) => {
        return formatTipoEnvio(cobranca?.tipoEnvio);
      },
      sortingEnabled: false,
    },
    {
      name: '',
      title: '',
      sortingEnabled: false,
      align: 'left',
      width: 130,
      getCellValue: (cobranca) => (
        <TableActionContainer>
          <TableActionButton
            icon={<FaEye size={10} />}
            title="Ver detalhe da cobrança"
            onClick={() => handleVisualizar(cobranca)}
          />
        </TableActionContainer>
      ),
    },
  ];

  useEffect(() => {
    if (search) {
      const filteredData = cobranca.filter((row) => {
        return (
          (row.cliente &&
            noAccents(row.cliente)
              .toLowerCase()
              .includes(noAccents(search).toLowerCase())) ||
          (row.nossoNumero &&
            row.nossoNumero
              .toString()
              .toLowerCase()
              .includes(search.toLowerCase())) ||
          (row.vencimento && row.vencimento.toLowerCase().includes(search.toLowerCase())) ||
          (row.valor && getCurrency(row.valor).includes(search)) ||
          (row.tipoEnvio &&
            row.tipoEnvio
              .toString()
              .toLowerCase()
              .includes(search.toLowerCase()))
        );
      });

      if (!isEqual(filteredData, filteredCobranca)) {
        setFilteredCobranca(filteredData);
      }
    } else {
      if (!isEqual(cobranca, filteredCobranca)) {
        setFilteredCobranca(cobranca);
      }
    }
  }, [search, cobranca, filteredCobranca]);

  return (
    <SimpleDialog fullScreen open={modal === AcompanharReguaCobrancaModais.visualizarCobranca}>
      <SimpleDialogContent
        innerScroll={false}
        header={{
          title: 'Visualizar cobranças',
          icon: <FaArrowCircleUp />,
          toolbar: (
            <div>
              <CancelButton onClick={handleCloseModal}>Fechar</CancelButton>
            </div>
          ),
          searchBar: (
            <>
              <PageHeader>
                <div>
                  <div className="stepBarraPesquisa">
                    <SearchBar searchText={search} onChange={setSearch} placeholder="Pesquisar" />
                  </div>
                </div>
              </PageHeader>
            </>
          ),
        }}
        content={
          <div className="h-full max-h-full w-full">
            <Grid container spacing={3}>
              <div style={{ width: '100%' }}>
                <SimpleTable
                  getRowId={(row) => row}
                  columns={columns}
                  rows={filteredCobranca}
                  hideSelection
                  fetchOnCreate
                  loading={loading}
                  defaultSorting={[{ columnName: 'nome', direction: 'desc' }]}
                  totalCount={filteredCobranca?.length}
                  onCurrentPageChange={(params) => {
                    setPage(params);
                  }}
                  onPageSizeChange={(params) => {
                    setSize(params);
                  }}
                  onSortingChange={(params) => console.log(params)}
                  onLoadingChange={(params) => console.log('loading change', params)}
                  searchBy={search} // Pass the search value here
                />
              </div>
            </Grid>
            <DetalhesCobrancaModal />
          </div>
        }
      />
    </SimpleDialog>
  );
};
export default VisualizarCobrancaModal;
